import Layer from "./Layer";
import Rect from "./Rect";
import Vector2 from "./Vector2";
import Scene from "./Scene";
import NodeLayer from "./NodeLayer";
import * as Util from "./Util";
import { observable } from "mobx";

function getHandleRadius(): number {
  if (typeof window == "undefined") {
    return 5;
  }

  return 5 * window.devicePixelRatio;
}

export default class ConnectionLayer extends Layer {
  private _startNode: NodeLayer;
  private _endNode: NodeLayer;
  private _endPoint: Vector2;
  private _handleRadius = getHandleRadius();
  private _midPoint = new Vector2();

  @observable
  private _condition = "";

  public get condition(): string {
    return this._condition;
  }

  public setCondition(cond: string) {
    this._condition = cond;
  }

  public get startNode(): NodeLayer {
    return this._startNode;
  }

  public get endNode(): NodeLayer {
    return this._endNode;
  }

  public get startPoint(): Vector2 {
    return this.startNode.boundingRect.getCenterPoint();
  }

  public get endPoint(): Vector2 {
    if (this.endNode != null) {
      return this.endNode.getCenter();
    }

    return this._endPoint;
  }

  public setMidPoint(midPoint: Vector2) {
    this._midPoint = midPoint;
  }

  public setEndPoint(ep: Vector2) {
    if (this.endNode) {
      return;
    }

    this._endPoint = ep;
    this.setBoundingRect(Rect.fromExtents(this.startPoint, this.endPoint));
  }

  public setEndNode(node: NodeLayer) {
    this._endNode = node;
    this.setBoundingRect(Rect.fromExtents(this.startPoint, this.endPoint));
  }

  public constructor(scene: Scene, id: string, startNode: NodeLayer) {
    super(
      scene,
      id,
      new Rect(startNode.boundingRect.getCenterPoint(), new Vector2())
    );
    this._startNode = startNode;
    this._endPoint = startNode.boundingRect.getCenterPoint();
  }

  public checkPointIntersection(point: Vector2): boolean {
    var pt = this.scene.camera.transformPointWorldToScreen(point);
    var mag = pt.sub(this._midPoint).magnitude();
    return mag < this._handleRadius * 2;
  }

  public getEndPointIntersection(): Vector2 {
    if (this._endNode == null) {
      return this.endPoint;
    }

    var startPoint = this.startPoint;
    var rect = this.endNode.boundingRect;
    var rectStartPoint = rect.position;
    var rectEndPoint = rect.getEndPoint();

    var endPoint = Util.pointOnRect(
      startPoint.x,
      startPoint.y,
      rectStartPoint.x,
      rectStartPoint.y,
      rectEndPoint.x,
      rectEndPoint.y
    );

    if (endPoint == null) {
      return this.endPoint;
    }

    var direction = endPoint.sub(startPoint);
    var length = direction.magnitude();

    if (length < this._handleRadius) {
      return endPoint;
    }

    var sub = direction.mulScalar(
      (this._handleRadius * 1.8) / this.scene.camera.scale / length
    );

    return endPoint.sub(sub);
  }

  public getStartPointIntersection(): Vector2 {
    var startPoint = this.endPoint;
    var rect = this.startNode.boundingRect;
    var rectStartPoint = rect.position;
    var rectEndPoint = rect.getEndPoint();

    var endPoint = Util.pointOnRect(
      startPoint.x,
      startPoint.y,
      rectStartPoint.x,
      rectStartPoint.y,
      rectEndPoint.x,
      rectEndPoint.y
    );

    if (endPoint == null) {
      return this.startPoint;
    }

    return endPoint;
  }
}
